import { TutorialDefinition } from '../TutorialEngine.js';
import { MirrorsRenderOptions } from './MirrorsTutorialAdapter.js';
import { COLORS, colorWithAlpha } from '../../rendering/Renderer.js';

type MirrorsScene = {
    gameState: string;
    renderOptions: MirrorsRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// New reference game state for mirrors tutorial:
// C0b,C5f,A5b,A1f,F1b,F5b

const scenes: MirrorsScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'C0b,C5f,A1f,A5b,F1b,F5b',
        renderOptions: { showLaser: true, showReverseLaser: true },
        annotations: [
            { type: 'text-label', text: 'In Mirrors, place mirrors\nto hit all digits in order', cell: 'C2', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Entry, exit, and beam travel
    {
        gameState: 'F0b,F3f',
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'highlight', cells: ['A0'], color: colorWithAlpha('teal', 0.2) },
            { type: 'highlight', cells: ['F5'], color: colorWithAlpha('teal', 0.2) },
            { type: 'text-label', text: 'Beam enters here', cell: 'A0', anchor: 'far-right' },
            { type: 'text-label', text: 'It travels in\na straight line', cell: 'C2', anchor: 'center' },
            { type: 'text-label', text: 'It must exit here after\nhitting all digits in order', cell: 'F5', anchor: 'far-left' },
        ],
    },

    // Scene 3: Mirror placement
    {
        gameState: 'C0b,C4f',
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Beam travels in\nstraight lines', cell: 'C0', anchor: 'right' },
            { type: 'text-label', text: 'Mirrors reflect the beam', cell: 'C2', anchor: 'center' },
            { type: 'text-label', text: 'Click to place\nmirrors and adjust\ntheir orientation', cell: 'E4', anchor: 'center' },
            { type: 'pulse', cell: 'C4', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: ['C0b,C4f', 'C0b,C4b', 'C0b']
        },
    },

    // Scene 4: Cannot place on digits
    {
        gameState: '',
        renderOptions: { showLaser: false, showReverseLaser: false },
        annotations: [
            { type: 'exclusion', cells: ['E1', 'C3', 'A4'] },
            { type: 'text-label', text: 'No mirrors allowed\non digit cells', cell: 'C0', anchor: 'center' },
        ],
    },

    // Scene 5: Hit in order, no double hits
    {
        gameState: 'C0b,C5b,E5f',
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Digits must be\nhit in order', cell: 'E0', anchor: 'center', color: COLORS.red },
            { type: 'text-label', text: 'Each digit can\nonly be hit once', cell: 'E4', anchor: 'center', color: COLORS.red },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: ['C0b,C5b,E5f,A5b,A3f', 'C0b,C5f,E5f,A5b,A3f'],
        },
    },


    // Scene 6: Hint beam and unused mirrors
    {
        gameState: 'C0b,C5b,D5f,A1f,E5b,D2f,D4b',
        renderOptions: { showLaser: true, showReverseLaser: true },
        annotations: [
            { type: 'text-label', text: 'Amber hint beam\nis reversed', cell: 'E4', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'Amber mirrors\nare unused', cell: 'B2', anchor: 'center', color: COLORS.amber },
        ],
    },

];

/**
 * Mirrors tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/mirrors.md for detailed scene descriptions.
 */
export const MIRRORS_TUTORIAL: TutorialDefinition = {
    gameType: 'mirrors',
    gridCode: '6/1-3:1C3,2A4,3E1',
    scenes,
};
